// websocket/forced-movement-plan.ts — Lane MV4 S2 (2026-09-28): the browser binding of the push / pull displacement twin
// (janus-engine/movement/forced-movement.ts) over the page's own encounter rows — what Lane A's post-hit dispatch calls
// (cross-lane ASK: `check_push_and_pull_after_hit` → `window.ftForcedMovementPlan` / `planForcedMovement`). It plans; it never
// commits (Lane A's commit writes the positions; collision / fall damage stays on its damage writer, PHP's fall after the commit).
import { resolveForcedMovement, type ForcedMoveReaders, type ForcedMoveResult } from '../janus-engine/movement/forced-movement.js';
import { movementScopeReaders } from './movement-truth.js';
import { encounterShapeForReaders } from '../janus-engine/geometry/encounter-shape.js';
import { encounterField } from '../shared/array-types.js';
import type { RestShape } from '../janus-engine/inventory/floor-move.js';
import { getSwarmTemplateForCreature } from '../shared/swarm-templates.js';
import { sizeWidthFt } from '../encounter-map/ft3d/perception.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => asRow(v) !== null) as Row[] : [];
const upper = (value: unknown): string => String(Array.isArray(value) ? value[0] : value ?? '').trim().toUpperCase();
const TRANSITION_PROPS = new Set(['9A88314E-7EDA-71C8-C98D-04DE57894484', 'ABE07D2F-7E8D-E61E-BCEE-AD1C2435C9D8', 'BF4DEA68-3856-F56D-7C6F-082BCA0E5450']);
const CURTAIN_ITEM_ID = 'E35239DD-DDAD-4C19-9AE6-1EE4FF9C1299';

export function sceneShape(encounter: Row): RestShape | null {
  const stored = encounterField<unknown>(encounter, 'encounter_shape');
  // Absent shapes keep legacy centre coordinates. Stored geometry uses the
  // shared runtime reader; authoring metadata does not decide physical support.
  return stored === undefined || stored === null || stored === '' ? null
    : encounterShapeForReaders(encounter) as RestShape | null;
}
const layerHas = (row: Row | null, key: string): boolean => !!row && (ftLayerValue(row, key) != null || (ftLayerEntries(row, key) ?? []).length > 0);

/** The ForcedMoveReaders over the page's encounter (movementScopeReaders + the sweep's body / defence / pass-through facts). */
export function forcedMovementReaders(encounter: Row): ForcedMoveReaders {
  const base = movementScopeReaders(encounter);
  const definition = (id: string): Row | null => base.definition(upper(id)) ?? base.definition(id);
  return {
    ...base,
    // The swept square and the landing test use the SIZE-TIER base (ft_ground_occupancy_base_size); a mesh body's collision box is
    // not what get_object_rectangle() sweeps — not provable here.
    // A swarm's occupied frontage widens with its vitality (Codex S2 F3) — the size tier is not its sweep width.
    baseSizeFt: row => { if (getSwarmTemplateForCreature(row as never)) return null; const g = base.geometry(row); return g && g.rulesDiameter === undefined && g.diameter > 0 ? g.diameter : null; },
    heightFt: row => { const g = base.geometry(row); return g && g.height > 0 ? g.height : null; },
    // janus_forced_movement_defense_sources(): the compiled slot, else resolve_adv_dis_layer_sources() over every carrier, else the
    // root layer. Only a provable answer returns a boolean (Codex S2 F7) — a carrier / equipped source is left to PHP (null).
    immuneToForcedMovement: row => {
      const defenses = asRow(asRow(asRow(row.computed)?.creature)?.defenses);
      if ((Array.isArray(defenses?.immune_to_forced_movement) && defenses.immune_to_forced_movement.length > 0) || layerHas(row, 'immune_to_forced_movement')) return true;
      if (layerHas(row, 'immune_to_forced_movement_from_size_category_number_below')) return null;
      for (const status of base.statuses(row)) {
        const def = asRow(status.status_arr) ?? definition(String(status.status_item_id ?? ''));
        if (!def) return null;
        if (layerHas(def, 'immune_to_forced_movement')) return true;
        if (layerHas(def, 'immune_to_forced_movement_from_size_category_number_below')) return null;
      }
      const carrier = (entry: unknown): boolean | null => {
        const wrapper = asRow(entry);
        const id = upper(asRow(wrapper?.items)?.item_id ?? wrapper?.item_id ?? entry);
        const source = asRow(wrapper?.item_arr) ?? (wrapper && Object.keys(wrapper).length > 1 ? wrapper : null) ?? definition(id);
        if (!source) return null;
        return layerHas(source, 'immune_to_forced_movement') || layerHas(source, 'immune_to_forced_movement_from_size_category_number_below') ? null : false;
      };
      for (const section of ['creature_type_arr', 'creature_sub_type_arr', 'lineage_arr', 'class_arr', 'subclass_arr', 'background_arr', 'feat']) {
        for (const entry of Array.isArray(row[section]) ? row[section] as unknown[] : []) if (carrier(entry) === null) return null;
      }
      for (const bucket of ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability']) {
        for (const entry of base.entries(row, bucket)) if (carrier(entry) === null) return null;
      }
      for (const raw of base.entries(row, 'inventory')) {
        const inv = asRow(raw);
        if (!inv) return null;
        if (/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(String(ftInventoryField(inv, 'equipped') ?? asRow(inv.inventory)?.equipped ?? ''))) return null;
      }
      return false;
    },
    // A plain size-tier row reaches half its tier width; a transformed / generated row (a mesh, a wall run, a scene prop) has a
    // footprint only its own geometry knows — not provable here (Codex S3 F3).
    inventoryExtentFt: inv => {
      const bucket = asRow(inv.inventory) ?? {};
      if (bucket.inventory_transform_3d != null || bucket.inventory_generation != null || inv.inventory_transform_3d != null || inv.inventory_generation != null) return null;
      const width = sizeWidthFt(inv);
      return Number.isFinite(width) && width > 0 ? width / 2 : null;
    },
    inventoryPassThrough: inv => {
      const bucket = asRow(inv.inventory) ?? {};
      const itemId = upper(bucket.inventory_item_id ?? asRow(inv.items)?.item_id);
      const name = String(bucket.inventory_name ?? '').toLowerCase();
      if (TRANSITION_PROPS.has(itemId) || /stair|ladder|ramp/.test(name)) return true;
      if (itemId === CURTAIN_ITEM_ID) return null; // ft_inventory_is_bound_curtain() reads the shape's bindings
      const def = definition(itemId);
      if (!def) return null;
      return layerHas(def, 'object_does_not_grant_environmental_cover');
    },
  };
}

export interface ForcedMovementRequest { encounterId: string; casterId: string; targetId: string; push?: number; pull?: number; toCasterCenter?: boolean; aoe?: Row | null }
/** Plan one push / pull on the page's encounter as it stands (no mutation). */
export function planForcedMovement(request: ForcedMovementRequest): ForcedMoveResult {
  const encounter = asRow(window.FateTide?.encounterCache?.get(request.encounterId));
  if (!encounter) return { kind: 'handback', reason: 'forced-move:encounter-not-held' };
  const find = (id: string): Row | null => rows(encounter.encounter_creatures_arr).find(r => upper(asRow(r.encounter_creatures)?.encounter_creature_id) === upper(id)) ?? null;
  const caster = find(request.casterId), target = find(request.targetId);
  if (!caster || !target) return { kind: 'handback', reason: 'forced-move:row-not-held' };
  return resolveForcedMovement({ encounter, shape: sceneShape(encounter), caster, target, aoe: request.aoe ?? null,
    push: Number(request.push ?? 0), pull: Number(request.pull ?? 0), toCasterCenter: !!request.toCasterCenter }, forcedMovementReaders(encounter));
}
Object.assign(window, { ftForcedMovementPlan: planForcedMovement });
